/**
 * Die lokale Datenbank im Browser.
 *
 * **Die App schreibt immer zuerst hierhin.** Erst danach — wenn Netz da ist —
 * geht dieselbe Änderung an den Server. Das ist der Unterschied zwischen einer
 * App, die im Funkloch nichts kann, und einer, die einfach weiterläuft.
 *
 * Auf iOS ist IndexedDB die einzige Ablage, die für diese Datenmenge trägt;
 * `localStorage` ist zu klein und synchron.
 */

import Dexie from 'dexie';
import type { EntityTable } from 'dexie';
import { normalizeSteps } from '@kraftwerk/shared';
import type {
  DishIngredient,
  FrozenDish,
  HouseholdMealStatus,
  MealExtra,
  MealRules,
  RecordFields,
  RecordKind,
  RecordPlan,
  RecordVisibility,
  AmountUnit,
  BodyRegion,
  DaySlot,
  Equipment,
  ExerciseVideo,
  FoodLogEstimate,
  PhotoEstimate,
  MacroSource,
  MealSlot,
  MovementPattern,
  Muscle,
  Mutation,
  Nutrients,
  PocketKind,
  StarKind,
  CheckinKind,
  CheckinNotice,
  ShelfCategory,
  SupplementForm,
  SyncCursor,
  Measurements,
  SyncTable,
  SyncedRow,
  TrackingMode,
} from '@kraftwerk/shared';

export interface LocalSupplement extends SyncedRow {
  userId?: string;
  name: string;
  dose: number | null;
  unit: string | null;
  scheduleTimes: string[];
  weekdays: number[];
  months: number[];
  /** Zur Mahlzeit. Fehlt bei Zeilen, die vor dieser Änderung auf dem Gerät lagen. */
  mealSlot?: MealSlot | null;
  note: string | null;
  sourceNote: string | null;
  active: boolean;
  sortOrder: number;
  /** Seit 16.10.2026; ältere Zeilen haben die Felder nicht. */
  form?: SupplementForm | null;
  barcode?: string | null;
}

export interface LocalIntake extends SyncedRow {
  userId?: string;
  supplementId: string;
  dueAt: string;
  acknowledgedAt: string | null;
  skippedAt: string | null;
  skipReason: string | null;
}

// --- Training ----------------------------------------------------------------

/**
 * Eine Übung, wie sie auf dem Gerät liegt.
 *
 * `parentId` bindet eine Variante an ihren Grundtyp — Bankdrücken Kurzhantel
 * unter Bankdrücken. Geloggt wird immer auf der Variante: 60 kg auf der
 * Schrägbank sind nicht 60 kg auf der Flachbank.
 */
export interface LocalExercise extends SyncedRow {
  userId?: string;
  parentId: string | null;
  catalogKey: string | null;
  name: string;
  variantLabel: string | null;
  trackingMode: TrackingMode;
  equipment: Equipment;
  primaryMuscles: Muscle[];
  secondaryMuscles: Muscle[];
  movementPattern: MovementPattern;
  bodyRegion: BodyRegion;
  isCompound: boolean;
  isUnilateral: boolean;
  defaultSets: number;
  repMin: number;
  repMax: number;
  incrementKg: number;
  restSeconds: number;
  videos: ExerciseVideo[];
  cues: string[];
  setupNotes: string | null;
  note: string | null;
  isFavorite: boolean;
  sortOrder: number;
}

export interface LocalPlan extends SyncedRow {
  userId?: string;
  catalogKey: string | null;
  name: string;
  note: string | null;
  active: boolean;
  startedAt: string | null;
  sortOrder: number;
}

export interface LocalPlanDay extends SyncedRow {
  userId?: string;
  planId: string;
  catalogKey: string | null;
  name: string;
  position: number;
}

export interface LocalPlanExercise extends SyncedRow {
  userId?: string;
  planDayId: string;
  exerciseId: string;
  position: number;
  sets: number;
  repMin: number;
  repMax: number;
  restSeconds: number | null;
  note: string | null;
  /** Supersatz mit der Übung davor. Fehlt bei Zeilen von vor dem 23.10.2026. */
  supersetWithPrevious?: boolean;
}

export interface LocalWorkoutSession extends SyncedRow {
  userId?: string;
  planDayId: string | null;
  title: string;
  startedAt: string;
  endedAt: string | null;
  bodyWeightKg: number | null;
  note: string | null;
  /** Seit E7: „Wie lief's?" 1–5. Fehlt bei älteren Zeilen auf dem Gerät. */
  rating?: number | null;
  /** „Nur heute tauschen“: geplante Übung → Ersatz. Fehlt bei älteren Zeilen. */
  swaps?: Record<string, string> | null;
}

export interface LocalWorkoutSet extends SyncedRow {
  userId?: string;
  sessionId: string;
  exerciseId: string;
  position: number;
  weightKg: number | null;
  reps: number | null;
  seconds: number | null;
  distanceM: number | null;
  bodyWeightKg: number | null;
  isWarmup: boolean;
  side: 'left' | 'right' | null;
  completedAt: string | null;
  note: string | null;
}

// --- Ernährung ---------------------------------------------------------------

export interface LocalFood extends SyncedRow {
  userId?: string;
  name: string;
  brand: string | null;
  kcalPer100g: number;
  proteinPer100g: number;
  carbsPer100g: number;
  /** Weitere Nährstoffe je 100 g (16.10.2026); fehlt das Feld: unbekannt. */
  nutrients?: Nutrients | null;
  fatPer100g: number;
  portionName: string | null;
  portionGrams: number | null;
  sourceNote: string | null;
  barcode: string | null;
  isFavorite: boolean;
  /** Seit E6. Ältere Zeilen auf dem Gerät haben das Feld nicht — dann gilt `other`. */
  category?: ShelfCategory;
}

export interface LocalRecipe extends SyncedRow {
  userId?: string;
  catalogKey: string | null;
  name: string;
  slot: MealSlot;
  portions: number;
  macroSource: MacroSource;
  kcalPerPortion: number | null;
  proteinPerPortion: number | null;
  carbsPerPortion: number | null;
  fatPerPortion: number | null;
  /** Weitere Nährstoffe je Portion, von Hand oder vom Import (16.10.2026). */
  nutrients?: Nutrients | null;
  items: string | null;
  /**
   * Seit E9 eine Liste. Zeilen von vor dem Update tragen noch Freitext, bis
   * Version 8 der lokalen Datenbank sie umstellt — gelesen wird deshalb über
   * `normalizeSteps`, nie direkt.
   */
  steps: string[] | string | null;
  note: string | null;
  sourceNote: string | null;
  isFavorite: boolean;
  /** Seit E6b. Was am Vorabend zu tun ist. Ältere Zeilen haben das Feld nicht. */
  prepNote?: string | null;
  /** Seit E9. Woher das Rezept stammt. */
  sourceUrl?: string | null;
  /** Seit E9. Setzt nur der Server; `null`/fehlend = kein Bild. */
  imageAt?: string | null;
}

export interface LocalRecipeIngredient extends SyncedRow {
  userId?: string;
  recipeId: string;
  /** `null` = freie Zeile („1 Prise Salz") ohne Nährwerte (seit E9). */
  foodId: string | null;
  /** Maßgeblich für Nährwerte und Einkauf; bei freien Zeilen `null`. */
  grams: number | null;
  /** Eingegeben als Portionen des Lebensmittels — nur Anzeige (seit E9). */
  portionCount?: number | null;
  /** Text der freien Zeile (seit E9). */
  label?: string | null;
  position: number;
}

/** Kochnotiz: Wie war es diesmal? (seit E9) */
export interface LocalRecipeCookNote extends SyncedRow {
  userId?: string;
  recipeId: string;
  cookedOn: string;
  rating: 1 | -1 | null;
  note: string | null;
}

export interface LocalMealPlanEntry extends SyncedRow {
  userId?: string;
  date: string;
  slot: DaySlot;
  recipeId: string | null;
  foodId: string | null;
  portions: number;
  grams: number | null;
  note: string | null;
}

/** Eingefroren: Name und Nährwerte gelten so, wie sie beim Essen galten. */
export interface LocalFoodLogEntry extends SyncedRow {
  userId?: string;
  date: string;
  slot: DaySlot;
  eatenAt: string;
  recipeId: string | null;
  foodId: string | null;
  label: string;
  portions: number | null;
  grams: number | null;
  kcal: number;
  protein: number;
  /** Eingefroren wie die Makros (16.10.2026). */
  nutrients?: Nutrients | null;
  carbs: number;
  fat: number;
  note: string | null;
  /** „Schätzung per Foto (KI)“ (27.10.2026) — bleibt, auch wenn Werte bearbeitet wurden. */
  estimate?: FoodLogEstimate | null;
}

/** Ein Tagebucheintrag. Alle Werte optional — nicht eingetragen ist `null`, nicht 0. */
export interface LocalJournalEntry extends SyncedRow {
  userId?: string;
  date: string;
  sleepHours: number | null;
  sleepQuality: number | null;
  energy: number | null;
  mood: number | null;
  soreness: number | null;
  note: string | null;
  /** Seit E10. Ältere Zeilen haben das Feld nicht — gilt dann als nicht gewogen. */
  bodyWeightKg?: number | null;
  /** Körpermaße seit 23.10.2026; fehlt bei älteren Zeilen — dann nicht gemessen. */
  measurements?: Measurements | null;
  /** Schritte aus Apple Health per Kurzbefehl (24.10.2026); schreibt nur der Server. */
  stepCount?: number | null;
}

/** Beschreibung eines Tagebuchfotos. Das Bild selbst liegt im `photo_blobs` oder beim Server. */
export interface LocalJournalPhoto extends SyncedRow {
  userId?: string;
  journalEntryId: string;
  width: number;
  height: number;
  bytes: number;
  caption: string | null;
  /** Vom Server gesetzt, sobald das Bild dort liegt. Der Client schreibt das nie. */
  uploadedAt?: string | null;
  /** Gehört zu einem Gedanken (E14) — sonst zum Tag. Fehlt bei älteren Zeilen. */
  noteId?: string | null;
}

/** Ein Gedanke im Tagebuch (E14). Der verknüpfte Termin ist eingefroren. */
export interface LocalJournalNote extends SyncedRow {
  userId?: string;
  date: string;
  time: string;
  title: string | null;
  body: string;
  mood: number | null;
  tags: string[];
  eventKind: 'event' | 'household' | 'feed' | 'training' | null;
  eventId: string | null;
  eventTitle: string | null;
  eventDate: string | null;
}

/**
 * Ein Bild, das noch hochgeladen werden muss — **nur auf diesem Gerät**, nie
 * synchronisiert. Es bleibt, bis der Server es angenommen hat; offline
 * aufgenommen heißt also: wartet, nicht verloren.
 */
export interface LocalPhotoBlob {
  id: string;
  blob: Blob;
  createdAt: string;
  attempts: number;
  lastError?: string;
  /**
   * Beim Server angekommen (25.10.2026): Die Kopie bleibt, bis der Abgleich den
   * Vermerk `uploadedAt` bringt — sonst hätte das Gerät dazwischen weder Bild
   * noch Vermerk und behauptete „liegt auf einem anderen Gerät“.
   */
  uploadedAt?: string;
}

/**
 * Foto-Schätzung im Hintergrund (28.10.2026) — der Platzhalter im Tag.
 *
 * **Nie im Sync.** Er ist kein `food_log`-Eintrag: Erst „Passt“ oder
 * „Anpassen“ macht daraus eine normale Protokollzeile mit derselben Kennung
 * (`estimate = 'photo'`). Bis dahin zählt er in keiner Summe — weder mit
 * Werten noch als 0 —, die Anzeige sagt „enthält eine offene Schätzung“.
 *
 * Das Foto liegt nur hier (lokal), bis ein Ergebnis da ist oder verworfen
 * wird — damit „abgebrochen“ erneut gesendet werden kann. Aufträge anderer
 * Geräte desselben Kontos kommen über `/nutrition/photo-estimate/jobs`
 * hierher, ohne Foto.
 */
export interface LocalEstimateJob {
  /** Kennung des Auftrags — später die der Protokollzeile. */
  id: string;
  date: string;
  slot: DaySlot;
  /** `unsent`: noch nicht beim Server (offline o. Ä.). Sonst wie beim Server. */
  status: 'unsent' | 'queued' | 'running' | 'done' | 'failed';
  /** Stelle in der Schlange (0 = gerade dran). */
  position: number | null;
  /** Grund bei `failed` (`PHOTO_ESTIMATE_REASONS`, dazu `offline`). */
  reason: string | null;
  /** Geöffnetes, geprüftes Ergebnis. */
  result: PhotoEstimate | null;
  /** Noch nicht geöffnet (Gerät gesperrt): das Chiffrat wartet hier. */
  sealedResult: string | null;
  language: 'de' | 'en';
  createdAt: string;
  photo: Blob | null;
}

/**
 * Eine verschlüsselt geholte Zeile, die dieses Gerät noch nicht öffnen kann
 * (E21): Es ist nicht entsperrt. Sie wartet hier statt in ihrer Tabelle — so
 * sieht keine Ansicht einen leeren Umschlag als „Gedanke ohne Text“. Nach dem
 * Entsperren wandert sie geöffnet in ihre Tabelle (`openHeldRows`).
 */
export interface LocalSealedRow {
  /** `<tabelle>:<id>`. */
  key: string;
  table: string;
  row: SyncedRow;
}

/** Abend-Erinnerung zum Quittieren — vom Scheduler angelegt, wie eine Einnahme. */
export interface LocalPrepCheck extends SyncedRow {
  userId?: string;
  /** Der Tag, für den vorbereitet wird. */
  date: string;
  dueAt: string;
  acknowledgedAt: string | null;
  skippedAt: string | null;
}

/** Wiederkehrender Trainingstermin (E12). */
export interface LocalTrainingSlot extends SyncedRow {
  userId?: string;
  weekday: number;
  time: string;
  durationMin: number;
  active: boolean;
}

/** Einzelner Trainingstermin — verschoben, abgesagt, zusätzlich oder zur Erinnerung angelegt. */
export interface LocalTrainingDay extends SyncedRow {
  userId?: string;
  slotId: string | null;
  originDate: string | null;
  date: string;
  time: string;
  durationMin: number;
  status: 'planned' | 'cancelled';
  acknowledgedAt: string | null;
}

/** Felder eines eigenen Termins (E12) — privat und geteilt gleich. */
export interface EventFields {
  title: string;
  date: string;
  /** `HH:MM` oder `null` = ganztägig. */
  time: string | null;
  durationMin: number | null;
  endDate: string | null;
  location: string | null;
  note: string | null;
  activity: string | null;
  activityMin: number | null;
  cancelled: boolean;
}

export interface LocalEvent extends SyncedRow, EventFields {
  userId?: string;
}

export interface LocalHouseholdEvent extends SyncedRow, EventFields {
  userId?: string;
  householdId: string;
  /** Für wen (E16) — Mitglieder; leer = ohne Angabe. */
  forUserIds?: string[];
  /** Einkaufstag (18.10.2026). */
  shopping?: boolean;
  shopId?: string | null;
}

/** Eingelesener Termin — nur lesend, schreibt der Server. */
export interface LocalFeedEvent extends SyncedRow {
  userId?: string;
  feedId: string;
  title: string;
  startsAt: string;
  endsAt: string | null;
  allDay: boolean;
  date: string;
  endDate: string | null;
  location: string | null;
}

/** Eine Mahlzeit an einem Tag ausgelassen (16.10.2026). */
/** Getrunkenes (18.10.2026) — eine Zeile je Glas, nie ein Zähler. */
export interface LocalWaterEntry extends SyncedRow {
  userId?: string;
  date: string;
  ml: number;
  drankAt: string;
}

export interface LocalMealSkip extends SyncedRow {
  userId?: string;
  date: string;
  slot: DaySlot;
}

export interface LocalEventMark extends SyncedRow {
  userId?: string;
  feedEventId: string;
  activity: string | null;
  activityMin: number | null;
  cancelled: boolean;
}

/** Aufgabenliste (E12) — privat ohne, geteilt mit Haushalt. */
export interface LocalTaskList extends SyncedRow {
  userId?: string;
  householdId: string | null;
  name: string;
  kind: 'tasks' | 'checklist';
  position: number;
}

export interface LocalTask extends SyncedRow {
  userId?: string;
  householdId: string | null;
  listId: string;
  parentId: string | null;
  title: string;
  note: string | null;
  dueDate: string | null;
  dueTime: string | null;
  repeat: { every: number; unit: 'day' | 'week' | 'month' } | null;
  assigneeId: string | null;
  doneAt: string | null;
  doneBy: string | null;
  position: number;
  /** Taschengeld fürs Erledigen in Cent (E20); setzen nur Erwachsene. */
  rewardCents?: number | null;
  /** Sterne fürs Erledigen (26.10.2026); setzen nur Erwachsene. */
  rewardStars?: number | null;
}

export interface LocalTaskCompletion extends SyncedRow {
  userId?: string;
  householdId: string | null;
  listId: string;
  taskId: string | null;
  title: string;
  dueDate: string | null;
  doneAt: string;
  doneBy: string | null;
  /** Am Familien-Tablet abgehakt, das nicht für Belohnungen zählt (27.10.2026). */
  noReward?: boolean;
  itemsDone: number | null;
  itemsTotal: number | null;
}

// --- Haushalt ----------------------------------------------------------------

export interface LocalHousehold extends SyncedRow {
  userId?: string;
  name: string | null;
}

/** Nur lesbar — Mitgliedschaften ändert der Server (Beitritt, Austritt). */
export interface LocalHouseholdMember extends SyncedRow {
  userId: string;
  householdId: string;
  displayName: string;
  /** E16: `adult` oder `child` — ältere Zeilen ohne Feld sind Erwachsene. */
  role?: 'adult' | 'child';
  birthYear?: number | null;
  /** E20: Stufe eines Kindes, von den Eltern gesetzt — leer heißt `school`. */
  childLevel?: string | null;
  /** E20: Taschengeld-Regel — schreibt nur der Server. */
  allowanceCents?: number | null;
  allowanceInterval?: string | null;
  allowanceDay?: number | null;
  allowanceSince?: string | null;
  /** Geld für erledigte Aufgaben (E20) — ab Werk aus, schalten die Eltern an. */
  taskRewards?: boolean;
  rewardsSince?: string | null;
  /** Sterne für erledigte Aufgaben (26.10.2026) und die Belohnung dafür. */
  taskStars?: boolean;
  starsSince?: string | null;
  starGoalName?: string | null;
  starGoalStars?: number | null;
  /** Standort-Check-ins eines Kindes (27.10.2026) — schalten nur die Eltern an. */
  locationSharing?: boolean;
  /** Ernährung für ein Teen (27.10.2026) — schalten nur die Eltern an. */
  teenNutrition?: boolean;
  /** Training für ein Teen (28.10.2026) — schalten nur die Eltern an. */
  teenTraining?: boolean;
}

/** Kosten (E17) — alle teilbar: ohne `householdId` privat. Geld in Cent. */
export interface LocalExpenseCategory extends SyncedRow {
  userId?: string;
  householdId: string | null;
  key: string | null;
  name: string | null;
  budgetCents: number | null;
}

export interface LocalRecurringExpense extends SyncedRow {
  userId?: string;
  householdId: string | null;
  title: string;
  amountCents: number;
  category: string;
  categoryId: string | null;
  interval: 'monthly' | 'yearly';
  day: number;
  month: number | null;
  startDate: string;
  endDate: string | null;
  paidBy: string | null;
  active: boolean;
}

export interface LocalExpense extends SyncedRow {
  userId?: string;
  householdId: string | null;
  paidBy: string | null;
  amountCents: number;
  category: string;
  categoryId: string | null;
  date: string;
  note: string | null;
  source: 'manual' | 'shopping' | 'recurring';
  recurringId: string | null;
}

/** Bucketliste (18.10.2026) — privat (geheim) oder im Haushalt geteilt. */
export interface LocalBucketItem extends SyncedRow {
  userId?: string;
  householdId: string | null;
  title: string;
  note: string | null;
  status: 'idea' | 'planned' | 'done';
  eventId: string | null;
  eventDate: string | null;
  doneDate: string | null;
}

export interface LocalSavingsGoal extends SyncedRow {
  userId?: string;
  householdId: string | null;
  name: string;
  targetCents: number;
}

export interface LocalSavingsDeposit extends SyncedRow {
  userId?: string;
  householdId: string | null;
  goalId: string;
  amountCents: number;
  date: string;
}

/** Notiz über ein Kind (E16) — wer sie sieht, sagt `visibility`. */
export interface LocalMemberNote extends SyncedRow {
  userId?: string;
  householdId: string;
  aboutUserId: string;
  visibility: 'me' | 'adults' | 'child';
  date: string;
  body: string;
}

/**
 * Taschengeld (E20): eine Buchung auf dem Konto eines Kindes. Immer positiv,
 * `kind` sagt, ob plus oder minus. Wer eingetragen hat, steht in `userId`.
 */
export interface LocalPocketMoney extends SyncedRow {
  userId?: string;
  householdId: string;
  childId: string;
  kind: PocketKind;
  amountCents: number;
  /** Sparziel (24.10.2026); fehlt bei älteren Zeilen. */
  goalId?: string | null;
  date: string;
  note: string | null;
}

/** Sterne eines Kindes (26.10.2026) — kein Geld, eigene Zeilen. */
export interface LocalStarEntry extends SyncedRow {
  userId?: string;
  householdId: string;
  childId: string;
  kind: StarKind;
  stars: number;
  date: string;
  note: string | null;
}

/** Sparziel eines Kindes (E20) — gespart wird über Buchungen mit `goalId`. */
export interface LocalPocketGoal extends SyncedRow {
  userId?: string;
  householdId: string;
  childId: string;
  name: string;
  targetCents: number;
}

/** Name und Regal eingefroren — die Liste gehört dem Haushalt, Lebensmittel einem Konto. */
export interface LocalShoppingItem extends SyncedRow {
  userId?: string;
  householdId: string;
  name: string;
  amount: number | null;
  unit: AmountUnit | null;
  category: ShelfCategory;
  note: string | null;
  fromPlan: boolean;
  checkedAt: string | null;
  /** Laden (18.10.2026); fehlt/`null` = egal. */
  shopId?: string | null;
}

/**
 * Haushalts-Essensplan (27.10.2026): Eintrag oder Wunsch des Haushalts,
 * das Gericht eingefroren. Beim Server mit dem Haushaltsschlüssel.
 */
export interface LocalHouseholdMeal extends SyncedRow {
  userId?: string;
  householdId: string;
  status: HouseholdMealStatus;
  date: string | null;
  slot: DaySlot | null;
  dish: FrozenDish;
  note: string | null;
  cookId: string | null;
}

/** Wer isst mit (27.10.2026) — eine Zeile je Eintrag und Person, nur wenn abweichend. */
export interface LocalHouseholdMealEater extends SyncedRow {
  userId?: string;
  householdId: string;
  entryId: string;
  eaterId: string;
  portions: number;
  items: DishIngredient[];
  variant: string | null;
  vegetarian: boolean;
}

/** Persönliche Abweichung (27.10.2026); ohne `entryId` die Dauerregeln. */
export interface LocalMealDeviation extends SyncedRow {
  entryId: string | null;
  skip: boolean;
  factor: number | null;
  extras: MealExtra[];
  variant: FrozenDish | null;
  rules: MealRules | null;
}

/** Laden eines Haushalts (18.10.2026), mit eigener Regalfolge. */
export interface LocalShop extends SyncedRow {
  userId?: string;
  householdId: string;
  name: string;
  shelfOrder: string[] | null;
  position: number;
}

export interface LocalPantryItem extends SyncedRow {
  userId?: string;
  householdId: string;
  name: string;
  amount: number | null;
  unit: AmountUnit | null;
  category: ShelfCategory;
  note: string | null;
}

/** Ort des Haushalts (Standort, 27.10.2026) — beim Server nur verschlüsselt. */
export interface LocalPlace extends SyncedRow {
  userId?: string;
  householdId: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  radiusM: number | null;
}

/** Standort-Check-in (27.10.2026) — beim Server nur verschlüsselt. */
export interface LocalCheckin extends SyncedRow {
  userId?: string;
  householdId: string;
  kind: CheckinKind | null;
  latitude: number | null;
  longitude: number | null;
  accuracyM: number | null;
  placeId: string | null;
  placeName: string | null;
  recordedAt: string | null;
  liveUntil: string | null;
  /** Mitteilung an den Haushalt — Klartext bis zum Senden, dann verschlüsselt. */
  notice?: CheckinNotice | string | null;
}

/**
 * Akte-Eintrag (27.10.2026): eigene Akte ohne Haushalt (`me`), die eines
 * Kindes im Haushalt. Beim Server nur verschlüsselt (`ALWAYS_SEALED_TABLES`).
 */
export interface LocalRecordEntry extends SyncedRow {
  userId?: string;
  householdId: string | null;
  subjectId: string;
  visibility: RecordVisibility;
  kind: RecordKind;
  fields: RecordFields;
  plan: RecordPlan | null;
}

/** Eine noch nicht gesendete Änderung. */
export interface OutboxEntry extends Mutation {
  /** Aufsteigend — die Reihenfolge des Sendens ist die Reihenfolge des Entstehens. */
  seq?: number;
  createdAt: string;
  /** Fehlversuche; wächst nur bei echten Ablehnungen, nicht bei fehlendem Netz. */
  attempts: number;
  lastError?: string;
}

export interface MetaEntry {
  key: string;
  value: string;
}

export class KraftwerkDatabase extends Dexie {
  supplements!: EntityTable<LocalSupplement, 'id'>;
  supplement_intakes!: EntityTable<LocalIntake, 'id'>;
  profiles!: EntityTable<SyncedRow, 'id'>;
  reminders!: EntityTable<SyncedRow, 'id'>;
  exercises!: EntityTable<LocalExercise, 'id'>;
  training_plans!: EntityTable<LocalPlan, 'id'>;
  plan_days!: EntityTable<LocalPlanDay, 'id'>;
  plan_exercises!: EntityTable<LocalPlanExercise, 'id'>;
  workout_sessions!: EntityTable<LocalWorkoutSession, 'id'>;
  workout_sets!: EntityTable<LocalWorkoutSet, 'id'>;
  foods!: EntityTable<LocalFood, 'id'>;
  recipes!: EntityTable<LocalRecipe, 'id'>;
  recipe_ingredients!: EntityTable<LocalRecipeIngredient, 'id'>;
  recipe_cook_notes!: EntityTable<LocalRecipeCookNote, 'id'>;
  meal_plan_entries!: EntityTable<LocalMealPlanEntry, 'id'>;
  food_log!: EntityTable<LocalFoodLogEntry, 'id'>;
  meal_skips!: EntityTable<LocalMealSkip, 'id'>;
  water_log!: EntityTable<LocalWaterEntry, 'id'>;
  shops!: EntityTable<LocalShop, 'id'>;
  places!: EntityTable<LocalPlace, 'id'>;
  location_checkins!: EntityTable<LocalCheckin, 'id'>;
  record_entries!: EntityTable<LocalRecordEntry, 'id'>;
  household_meals!: EntityTable<LocalHouseholdMeal, 'id'>;
  household_meal_eaters!: EntityTable<LocalHouseholdMealEater, 'id'>;
  meal_deviations!: EntityTable<LocalMealDeviation, 'id'>;
  bucket_items!: EntityTable<LocalBucketItem, 'id'>;
  prep_checks!: EntityTable<LocalPrepCheck, 'id'>;
  training_slots!: EntityTable<LocalTrainingSlot, 'id'>;
  training_days!: EntityTable<LocalTrainingDay, 'id'>;
  events!: EntityTable<LocalEvent, 'id'>;
  household_events!: EntityTable<LocalHouseholdEvent, 'id'>;
  feed_events!: EntityTable<LocalFeedEvent, 'id'>;
  event_marks!: EntityTable<LocalEventMark, 'id'>;
  task_lists!: EntityTable<LocalTaskList, 'id'>;
  tasks!: EntityTable<LocalTask, 'id'>;
  task_completions!: EntityTable<LocalTaskCompletion, 'id'>;
  member_notes!: EntityTable<LocalMemberNote, 'id'>;
  pocket_money!: EntityTable<LocalPocketMoney, 'id'>;
  pocket_goals!: EntityTable<LocalPocketGoal, 'id'>;
  star_entries!: EntityTable<LocalStarEntry, 'id'>;
  expense_categories!: EntityTable<LocalExpenseCategory, 'id'>;
  recurring_expenses!: EntityTable<LocalRecurringExpense, 'id'>;
  expenses!: EntityTable<LocalExpense, 'id'>;
  savings_goals!: EntityTable<LocalSavingsGoal, 'id'>;
  savings_deposits!: EntityTable<LocalSavingsDeposit, 'id'>;
  journal_entries!: EntityTable<LocalJournalEntry, 'id'>;
  journal_photos!: EntityTable<LocalJournalPhoto, 'id'>;
  journal_notes!: EntityTable<LocalJournalNote, 'id'>;
  photo_blobs!: EntityTable<LocalPhotoBlob, 'id'>;
  estimate_jobs!: EntityTable<LocalEstimateJob, 'id'>;
  sealed_rows!: EntityTable<LocalSealedRow, 'key'>;
  households!: EntityTable<LocalHousehold, 'id'>;
  household_members!: EntityTable<LocalHouseholdMember, 'id'>;
  shopping_items!: EntityTable<LocalShoppingItem, 'id'>;
  pantry_items!: EntityTable<LocalPantryItem, 'id'>;
  outbox!: EntityTable<OutboxEntry, 'seq'>;
  meta!: EntityTable<MetaEntry, 'key'>;

  constructor(name = 'kraftwerk') {
    super(name);
    this.version(1).stores({
      supplements: 'id, updatedAt, sortOrder, name',
      supplement_intakes: 'id, updatedAt, dueAt, supplementId',
      profiles: 'id, updatedAt',
      reminders: 'id, updatedAt, dueAt, status',
      outbox: '++seq, mutationId, table',
      meta: 'key',
    });

    // E4: Training. Die alten Tabellen bleiben unverändert — Dexie legt nur die
    // neuen an, bestehende Daten auf dem Gerät werden nicht angefasst.
    this.version(2).stores({
      exercises: 'id, updatedAt, parentId, name, sortOrder',
      training_plans: 'id, updatedAt, active, sortOrder',
      plan_days: 'id, updatedAt, planId, position',
      plan_exercises: 'id, updatedAt, planDayId, exerciseId, position',
      workout_sessions: 'id, updatedAt, startedAt, planDayId',
      // `[exerciseId+completedAt]` ist der Index hinter „letzte Werte": beim
      // Öffnen einer Übung im Gym die letzten Sätze finden, ohne alles zu lesen.
      workout_sets: 'id, updatedAt, sessionId, exerciseId, [exerciseId+completedAt]',
    });

    // E5: Ernährung. `date` ist der Index, an dem jede Tagesansicht hängt —
    // der Kalendertag in Ortszeit, kein Zeitstempel.
    this.version(3).stores({
      foods: 'id, updatedAt, name, isFavorite',
      recipes: 'id, updatedAt, slot, name, isFavorite',
      recipe_ingredients: 'id, updatedAt, recipeId, foodId',
      meal_plan_entries: 'id, updatedAt, date, [date+slot]',
      food_log: 'id, updatedAt, date, [date+slot]',
    });

    // E6: Haushalt. `householdId` ist der Index, über den beim Austritt
    // aufgeräumt wird — fremd gewordene Zeilen schickt der Server nicht mehr.
    this.version(4).stores({
      households: 'id, updatedAt',
      household_members: 'id, updatedAt, householdId, userId',
      shopping_items: 'id, updatedAt, householdId, checkedAt',
      pantry_items: 'id, updatedAt, householdId, name',
    });

    // E6b: Abend-Erinnerung. `date` ist der Tag, für den vorbereitet wird.
    this.version(5).stores({
      prep_checks: 'id, updatedAt, date',
    });

    // E7: Tagebuch, einer je Tag (gelesen wird der zuletzt geänderte).
    this.version(6).stores({
      journal_entries: 'id, updatedAt, date',
    });

    // E7b: Fotos. `photo_blobs` ist die einzige Tabelle, die nie synchronisiert
    // wird — sie ist die Warteschlange der Bilder bis zum Upload.
    this.version(7).stores({
      journal_photos: 'id, updatedAt, journalEntryId',
      photo_blobs: 'id, createdAt',
    });

    // E9: Kochnotizen; Schritte werden vom Freitext zur Liste. Umgestellt wird
    // auf dem Gerät, damit eine noch nicht gesendete Änderung nicht als Text
    // rausgeht und die Anzeige nicht zwei Formen kennen muss.
    this.version(8)
      .stores({ recipe_cook_notes: 'id, updatedAt, recipeId' })
      .upgrade((tx) =>
        tx
          .table('recipes')
          .toCollection()
          .modify((recipe: { steps?: unknown }) => {
            if (typeof recipe.steps === 'string') {
              const list = normalizeSteps(recipe.steps);
              recipe.steps = list.length > 0 ? list : null;
            }
          }),
      );

    // E12: Trainingstage. `date` ist der Index jeder Wochen- und Tagesansicht.
    this.version(9).stores({
      training_slots: 'id, updatedAt, weekday',
      training_days: 'id, updatedAt, date',
    });

    // E12: Kalender. `date` ist der Index jeder Ansicht; `householdId` wie bei
    // der Einkaufsliste, damit beim Austritt aufgeräumt werden kann.
    this.version(10).stores({
      events: 'id, updatedAt, date',
      household_events: 'id, updatedAt, householdId, date',
      feed_events: 'id, updatedAt, date, feedId',
      event_marks: 'id, updatedAt, feedEventId',
    });

    // E12: Aufgaben. `dueDate` für Heute und Kalender, `listId` für die Listen.
    this.version(11).stores({
      task_lists: 'id, updatedAt, householdId',
      tasks: 'id, updatedAt, listId, dueDate, householdId',
      task_completions: 'id, updatedAt, listId, doneAt, householdId',
    });

    // E14: Gedanken im Tagebuch; Fotos lassen sich je Gedanke finden.
    this.version(12).stores({
      journal_notes: 'id, updatedAt, date, eventId',
      journal_photos: 'id, updatedAt, journalEntryId, noteId',
    });

    // E16: Notizen über Kinder — je Kind und Haushalt auffindbar.
    this.version(13).stores({
      member_notes: 'id, updatedAt, aboutUserId, householdId',
    });

    // E17: Kosten — Ausgaben je Datum, Einzahlungen je Ziel.
    this.version(14).stores({
      expense_categories: 'id, updatedAt, householdId',
      recurring_expenses: 'id, updatedAt, householdId',
      expenses: 'id, updatedAt, date, householdId, recurringId',
      savings_goals: 'id, updatedAt, householdId',
      savings_deposits: 'id, updatedAt, goalId',
    });

    // Eigene Mahlzeiten (16.10.2026): ausgelassen je Tag.
    this.version(15).stores({
      meal_skips: 'id, updatedAt, date',
    });

    // Wasser (18.10.2026): Getrunkenes je Tag.
    this.version(16).stores({
      water_log: 'id, updatedAt, date',
    });

    // Läden (18.10.2026): je Haushalt.
    this.version(17).stores({
      shops: 'id, updatedAt, householdId',
    });

    // Bucketliste (18.10.2026).
    this.version(18).stores({
      bucket_items: 'id, updatedAt, householdId, status',
    });

    // Taschengeld (E20, 23.10.2026): je Kind.
    this.version(19).stores({
      pocket_money: 'id, updatedAt, childId, householdId',
    });

    // Sparziele des Kindes (24.10.2026).
    this.version(20).stores({
      pocket_goals: 'id, updatedAt, childId, householdId',
    });

    // Ende-zu-Ende (E21, 25.10.2026): verschlüsselt Geholtes, das noch wartet.
    this.version(21).stores({
      sealed_rows: 'key, table',
    });

    // Sterne für erledigte Aufgaben (26.10.2026).
    this.version(22).stores({
      star_entries: 'id, updatedAt, childId, householdId',
    });

    // Standort (27.10.2026): Orte und Check-ins des Haushalts.
    this.version(23).stores({
      places: 'id, updatedAt, householdId',
      location_checkins: 'id, updatedAt, householdId, userId',
    });

    // Akte (27.10.2026): eigene und die der Kinder.
    this.version(24).stores({
      record_entries: 'id, updatedAt, subjectId, householdId',
    });

    // Haushalts-Essensplan (27.10.2026): Plan und Wünsche, wer isst mit,
    // persönliche Abweichungen.
    this.version(25).stores({
      household_meals: 'id, updatedAt, householdId, date',
      household_meal_eaters: 'id, updatedAt, householdId, entryId',
      meal_deviations: 'id, updatedAt, entryId',
    });

    // Foto-Schätzung im Hintergrund (28.10.2026): Platzhalter je Tag, nie im Sync.
    this.version(26).stores({
      estimate_jobs: 'id, date, status',
    });
  }

  /**
   * Die lokale Tabelle zu einem Sync-Namen.
   *
   * Steht hier und nicht im Sync-Motor, weil beide Richtungen sie brauchen:
   * Der Motor legt Geholtes ab, die Schreibfunktionen legen Eigenes ab. Zwei
   * Abbildungen wären zwei Gelegenheiten, eine Tabelle zu vergessen.
   */
  tableOf(name: SyncTable) {
    switch (name) {
      case 'supplements':
        return this.supplements;
      case 'supplement_intakes':
        return this.supplement_intakes;
      case 'profiles':
        return this.profiles;
      case 'reminders':
        return this.reminders;
      case 'exercises':
        return this.exercises;
      case 'training_plans':
        return this.training_plans;
      case 'plan_days':
        return this.plan_days;
      case 'plan_exercises':
        return this.plan_exercises;
      case 'workout_sessions':
        return this.workout_sessions;
      case 'workout_sets':
        return this.workout_sets;
      case 'foods':
        return this.foods;
      case 'recipes':
        return this.recipes;
      case 'recipe_ingredients':
        return this.recipe_ingredients;
      case 'recipe_cook_notes':
        return this.recipe_cook_notes;
      case 'meal_plan_entries':
        return this.meal_plan_entries;
      case 'food_log':
        return this.food_log;
      case 'prep_checks':
        return this.prep_checks;
      case 'training_slots':
        return this.training_slots;
      case 'training_days':
        return this.training_days;
      case 'events':
        return this.events;
      case 'household_events':
        return this.household_events;
      case 'feed_events':
        return this.feed_events;
      case 'event_marks':
        return this.event_marks;
      case 'meal_skips':
        return this.meal_skips;
      case 'water_log':
        return this.water_log;
      case 'shops':
        return this.shops;
      case 'places':
        return this.places;
      case 'location_checkins':
        return this.location_checkins;
      case 'record_entries':
        return this.record_entries;
      case 'household_meals':
        return this.household_meals;
      case 'household_meal_eaters':
        return this.household_meal_eaters;
      case 'meal_deviations':
        return this.meal_deviations;
      case 'bucket_items':
        return this.bucket_items;
      case 'task_lists':
        return this.task_lists;
      case 'tasks':
        return this.tasks;
      case 'task_completions':
        return this.task_completions;
      case 'member_notes':
        return this.member_notes;
      case 'pocket_money':
        return this.pocket_money;
      case 'star_entries':
        return this.star_entries;
      case 'pocket_goals':
        return this.pocket_goals;
      case 'expense_categories':
        return this.expense_categories;
      case 'recurring_expenses':
        return this.recurring_expenses;
      case 'expenses':
        return this.expenses;
      case 'savings_goals':
        return this.savings_goals;
      case 'savings_deposits':
        return this.savings_deposits;
      case 'journal_entries':
        return this.journal_entries;
      case 'journal_photos':
        return this.journal_photos;
      case 'journal_notes':
        return this.journal_notes;
      case 'households':
        return this.households;
      case 'household_members':
        return this.household_members;
      case 'shopping_items':
        return this.shopping_items;
      case 'pantry_items':
        return this.pantry_items;
    }
  }

  /** Alle fachlichen Tabellen — für Aufräumen und Transaktionen. */
  get syncTables() {
    return [
      this.supplements,
      this.supplement_intakes,
      this.profiles,
      this.reminders,
      this.exercises,
      this.training_plans,
      this.plan_days,
      this.plan_exercises,
      this.workout_sessions,
      this.workout_sets,
      this.foods,
      this.recipes,
      this.recipe_ingredients,
      this.recipe_cook_notes,
      this.meal_plan_entries,
      this.food_log,
      this.meal_skips,
      this.water_log,
      this.shops,
      this.places,
      this.location_checkins,
      this.record_entries,
      this.household_meals,
      this.household_meal_eaters,
      this.meal_deviations,
      this.bucket_items,
      this.prep_checks,
      this.training_slots,
      this.training_days,
      this.events,
      this.feed_events,
      this.event_marks,
      this.journal_entries,
      this.journal_photos,
      this.journal_notes,
      this.households,
      this.household_members,
      this.shopping_items,
      this.pantry_items,
      this.household_events,
      this.task_lists,
      this.tasks,
      this.task_completions,
      this.member_notes,
      this.pocket_goals,
      this.pocket_money,
      this.star_entries,
      this.expense_categories,
      this.recurring_expenses,
      this.expenses,
      this.savings_goals,
      this.savings_deposits,
    ];
  }
}

export const db = new KraftwerkDatabase();

// --- Cursor-Verwaltung -------------------------------------------------------

const cursorKey = (table: SyncTable) => `cursor:${table}`;

export async function readCursors(
  database: KraftwerkDatabase = db,
): Promise<Partial<Record<SyncTable, SyncCursor>>> {
  const entries = await database.meta.where('key').startsWith('cursor:').toArray();
  return Object.fromEntries(
    entries.map((entry) => [entry.key.slice('cursor:'.length) as SyncTable, entry.value]),
  );
}

export async function writeCursor(
  table: SyncTable,
  cursor: SyncCursor,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: cursorKey(table), value: cursor });
}

export async function resetCursors(
  tables: readonly SyncTable[],
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.bulkDelete(tables.map(cursorKey));
}

const HOUSEHOLDS_KEY = 'households';

/** Die Haushalte, in denen das Konto laut letztem Abgleich Mitglied ist. */
export async function readHouseholdIds(database: KraftwerkDatabase = db): Promise<string[] | null> {
  const entry = await database.meta.get(HOUSEHOLDS_KEY);
  if (!entry) return null;
  try {
    const parsed: unknown = JSON.parse(entry.value);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : null;
  } catch {
    return null;
  }
}

export async function writeHouseholdIds(
  ids: readonly string[],
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: HOUSEHOLDS_KEY, value: JSON.stringify([...ids].sort()) });
}

/**
 * Alles Lokale verwerfen.
 *
 * Beim Abmelden Pflicht: sonst liegen die Daten des vorigen Nutzers auf dem
 * Gerät und tauchen beim nächsten Anmelden im falschen Konto wieder auf.
 */
export async function clearLocalData(database: KraftwerkDatabase = db): Promise<void> {
  // `photo_blobs` gehört dazu: Fotos des vorigen Kontos haben auf dem Gerät
  // nach dem Abmelden nichts verloren — und hochgeladen würden sie ins falsche.
  // `sealed_rows` und der Kontoschlüssel in `meta` gehen mit (E21), die
  // Platzhalter der Foto-Schätzung samt Foto ebenso (28.10.2026).
  const tables = [
    ...database.syncTables,
    database.outbox,
    database.meta,
    database.photo_blobs,
    database.sealed_rows,
    database.estimate_jobs,
  ];
  await database.transaction('rw', tables, async () => {
    await Promise.all(tables.map((table) => table.clear()));
  });
}

/**
 * Für Hintergrund-Schreiben aus Effekten (`void …`): Ist die Datenbank
 * inzwischen zu (Abmelden, Kontowechsel — in Tests der nächste Test), gibt
 * es nichts mehr zu schreiben. Jeder andere Fehler bleibt laut.
 */
export function ignoreClosed(error: unknown): void {
  if (error instanceof Error && error.name === Dexie.errnames.DatabaseClosed) return;
  throw error;
}
